<template>
  <div>
    <form :id="`form-new-${formIdentifier}`" @submit.prevent="submit">
      <template v-if="schema.fields.length > 0">
        <vue-form-generator
          :key="formUniqueId"
          :schema="schema"
          :model="model"
          :options="{ validationAfterLoad: true, validationAfterChanged: true }"
        />
        <slot name="beforeSubmit" />
        <b-row>
          <b-col>
            <slot name="submitButton">
              <b-button
                :disabled="isDisabled"
                block
                type="submit"
                variant="success"
              >
                {{ $t('actions.create') }}
              </b-button>
            </slot>
          </b-col>
        </b-row>
      </template>
      <div v-else class="text-center">
        <i class="ti ti-2x ti-spin ti-refresh" />
      </div>
    </form>
  </div>
</template>

<script>
  import VueFormGenerator from '@triotech/vue-core/src/vendor/vue-form-generator';
  import AbstractAjax from '@triotech/vue-core/src/mixins/AbstractAjax';

  export default {
    name: 'AjaxNewComponent',
    components: {
      'vue-form-generator': VueFormGenerator.component,
    },
    mixins: [
      AbstractAjax,
    ],
    props: {
      isDisabled: {
        type: Boolean,
        default: false,
      },
      name: {
        type: String,
        default: null,
      },
      uri: {
        type: String,
        default: null,
      },
      loadOnMount: {
        type: Boolean,
        default: true,
      },
      refreshAjaxIndex: {
        type: Boolean,
        default: false,
      },
      refAjaxIndex: {
        type: [String, Array],
        default: null,
      },
      closeModal: {
        type: Boolean,
        default: false,
      },
      refModal: {
        type: String,
        default: null,
      },
      defaultModelValues: {
        type: Object,
        default: null,
      },
      fieldsFilterList: {
        type: Array,
        default: () => [],
      },
      fieldsFilerInverse: {
        type: Boolean,
        default: false,
      },
      notificationSuccessText: {
        type: String,
        default() {
          return this.$t(`flashes.${this.name}.create`);
        },
      },
      config: {
        type: Object,
        default: () => ({}),
      },
      auth: {
        type: [Boolean, String],
        default: true,
      },
      authHeader: {
        type: String,
        default: 'Authorization',
      },
      authPrefix: {
        type: String,
        default: 'Bearer',
      },
      action: {
        type: String,
        default: 'new',
      },
      serializer: {
        type: Function,
        default: _.identity,
      },
      formId: {
        type: [Number, String],
        default: null,
      },
    },
    data() {
      return {
        model: () => ({}),
        schema: {
          fields: [],
        },
        uriOption: null,
        formUniqueId: null,
      };
    },
    computed: {
      getUri() {
        // if uriOption have some option , construct the uri with it!
        let uri = this.uri || this.name;
        if (this.uriOption) {
          if (this.uriOption.suffix) {
            uri += this.uriOption.suffix;
          }
        }
        return uri;
      },
      formIdentifier() {
        return this.formId || this.name;
      },
    },
    watch: {
      defaultModelValues: {
        deep: true,
        handler(newValue) {
          _.merge(this.model, newValue);
        },
      },
    },
    mounted() {
      this.formUniqueId = `${this.$moment().valueOf()}-new`;

      if (this.loadOnMount) {
        this.load();
      }

      if (!this.loadOnMount && this.refModal !== null) {
        this.$bus.$on(`t-event.t-modal.${this.refModal}.opened`, this.load);
      }

      this.$bus.$on(`t-event.ajax-new.${this.name}.submit`, this.submit);
    },
    created() {
      const auth = _.isBoolean(this.auth) ? 'oauth.access_token' : this.auth;
      if (auth) {
        const header = {};
        header[this.authHeader] = [this.authPrefix, _.get(this.get(), auth)].join(' ').trim();
        this.config.headers = _.extend(header, this.config.headers);
      }
    },
    beforeDestroy() {
      if (!this.loadOnMount && this.refModal !== null) {
        this.$bus.$off(`t-event.t-modal.${this.refModal}.opened`);
      }

      this.$bus.$off(`t-event.ajax-new.${this.name}.submit`);
    },
    methods: {
      applyFilterOnSchema() {
        // TODO apply with inverse option
        if (_.size(this.fieldsFilterList) > 0) {
          _.forEach(this.fieldsFilterList, (fieldFiltered) => {
            _.remove(this.schema.fields, s => s.model === fieldFiltered);
          });
        }
      },
      async load(dataEvent = null) {
        // if data is passed with event, store data in uriOption
        if (dataEvent) {
          this.$set(this, 'uriOption', dataEvent);
        }

        this.$bus.$emit(`t-event.ajax-new.${this.name}.loading`);
        this.$set(this, 'model', this.defaultModelValues || {});

        const data = await this.$ajax.get(`${this.getUri}/${this.action}`, {}, this.config);
        const form = this._.isPlainObject(data) ? data.form : data;

        if (this._.isPlainObject(data)) {
          this.$set(this, 'model', this._.clearModelForForm(data.entity, form, this.defaultModelValues || {}));
        }

        this.$set(this.schema, 'fields', this._.form(this.$t, form));
        this.applyFilterOnSchema();

        this.$nextTick(() => {
          this.$bus.$emit(`t-event.ajax-new.${this.name}.loaded`);
        });
      },
      async submit(extraModel) {
        if (!(extraModel instanceof Event)) {
          _.extend(this.model, extraModel);
        }
        // TODO https://monterail.github.io/vuelidate/

        await this.$ajax.post(`${this.getUri}/${this.action}`, this.serializer(this.model), this.config).then((data) => {
          if (data.status) {
            this.$notify({
              title: this.$t(`flashes.${this.name}.create_title`),
              text: this.notificationSuccessText,
              type: 'success',
            });
            if (this.closeModal) {
              this.$bus.$emit(`t-event.t-modal.${this.refModal}.close`);
            }
            this.$bus.$emit(`t-event.new-submit.${this.name}.success`, this.model);
          } else {
            this.$notify({
              title: this.$t(`flashes.${this.name}.create_title`),
              text: this.$t(`flashes.${this.name}.not_create`),
              type: 'error',
            });
          }

          if (this.refreshAjaxIndex) {
            if (this._.isArray(this.refAjaxIndex)) {
              this._.each(this.refAjaxIndex, (refAjaxIndex) => {
                this.$bus.$emit(`t-event.ajax-index.${refAjaxIndex}.refresh`);
              });
            } else {
              this.$bus.$emit(`t-event.ajax-index.${this.refAjaxIndex}.refresh`);
            }
          }
        }).catch(this.catchHandler);
        return false;
      },
    },
  };
</script>

<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// node_modules/@triotech/vue-core/src/components/ajax/AjaxNew.vue